// The one rule for "has the vendor resubmitted an answer the admin has not
// responded to yet". Lives in @l2u/shared because three places need it and
// they cannot share a package otherwise: the API's company status rollup
// (apps/api/src/lib/companyStatusRollup.ts), the admin "Reply needed" signal
// (apps/admin/src/admin/awaitingReply.ts) and the vendor "needs your action"
// rule (packages/web-shared/src/lib/reviewState.ts). Each used to restate it,
// and all three shared the same blind spot (below). Import it; never restate.

export type ResubmitTurnFields = {
  /** When the vendor pressed "Submit to admin" (POST .../resubmit). */
  resubmitted_at?: string | null;
  /** The admin's last published verdict. */
  reviewed_at?: string | null;
  /**
   * When the newest COUNTED admin comment was WRITTEN (its created_at — see
   * latestAdminCommentAt). A thread comment published by Notify does NOT stamp
   * reviewed_at, so without this an admin who replied on the thread after a
   * resubmit was invisible to the rule: the vendor saw nothing to action and
   * the admin kept seeing "Reply needed" — each waiting on the other.
   */
  latest_admin_comment_at?: string | null;
};

function ms(iso: string | null | undefined): number | null {
  if (iso == null) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

/**
 * True when the vendor resubmitted and the admin has done nothing since — no
 * verdict AND no counted admin comment written after the resubmit. Compared as
 * instants (Date.parse), not strings, because the stamps come back from
 * PostgREST with a variable-length fraction while JS writes ISO `Z` strings.
 */
export function hasPendingResubmit(s: ResubmitTurnFields): boolean {
  const resubmitted = ms(s.resubmitted_at);
  if (resubmitted === null) return false;
  const reviewed = ms(s.reviewed_at);
  const commented = ms(s.latest_admin_comment_at);
  const lastAdmin =
    reviewed === null ? commented : commented === null ? reviewed : Math.max(reviewed, commented);
  return lastAdmin === null || lastAdmin < resubmitted;
}

/**
 * When the newest COUNTED admin comment was written — its created_at, the
 * AUTHORSHIP time — or null when none counts.
 *
 * "Did the admin respond after the resubmit" is a question about when the
 * admin wrote, so a comment drafted BEFORE the resubmit and notified after it
 * does not hand the turn over: it answered an earlier round.
 *
 * WHICH rows count is separate, and SAFE BY DEFAULT: a HELD admin comment
 * (published_at null — written but not yet notified) is skipped, so a
 * vendor-facing caller or the server status rollup can pass the raw thread and
 * a held draft still cannot move the turn. `includeHeld: true` is for the ADMIN
 * review page only: there a held comment counts as the admin having acted —
 * the same way a staged verdict clears "Reply needed" through the reviewed_at
 * draft overlay.
 */
export function latestAdminCommentAt(
  comments:
    | { author_type: string; created_at: string; published_at?: string | null }[]
    | null
    | undefined,
  opts: { includeHeld?: boolean } = {},
): string | null {
  let best: string | null = null;
  let bestMs = -Infinity;
  for (const c of comments ?? []) {
    if (c.author_type !== 'admin') continue;
    // Visibility decides whether a row counts; authorship dates it.
    if (c.published_at == null && !opts.includeHeld) continue;
    const at = c.created_at;
    const t = ms(at);
    if (t !== null && t > bestMs) {
      bestMs = t;
      best = at;
    }
  }
  return best;
}
